import React from 'react';
import PropTypes from 'prop-types';
import { compose, lifecycle, withState } from 'recompose';
import { Trans } from 'react-i18next';
import { t } from '../../../../../modules/infra/i18n';
import UploadAvatar from '../../../../common/uploadPhotoForm/uploadAvatar';
import BasicInfo from '../../../../common/basicInfoForm/basicInfo';
import Input from '../../../../common/input';
import Textarea from '../../../../common/textarea';
import Tooltip from '../../../../common/tooltip';
import PaymentOptionsModal from '../containers/PaymentOptionsModal';
import Money from '../../../../common/money';
import { formatDateTime } from '../../../../common/helpers/times';
import { canUserAddCustomers, canUserModifyCustomers } from '../../../../common/helpers/users';
import LockNotice from './LockNotice';
import withToggle from '../../../../common/hoc/withToggle';
import Icon, { GLYPHS } from '../../../../common/icon';
import { useFeatureFlags } from '../../../../../modules/infra/feature-flags';
import { TextM } from '../../../../../modules/infra/dls/components/typography';
import { Tag } from '../../../shops/prepayment-settings/tag/tag.component';
import CheckboxSwitch from '../../../../common/checkboxSwitch';
import styles from './styles.module.scss';

const Profile = ({
  permissions,
  rewardCounter,
  timezone,
  customerId,
  owner,
  activeType,
  dateFormat,
  phoneFormatter,
  paymentModalOn,
  paymentModalOff,
  paymentModalIsOn,
  setIsBlocked,
  isBlocked,
  blockStatusCanBeChanged,
  leadAutoAssigned,
}) => {
  const isSquireOwnedClient = !!customerId && !!owner;
  const canModifyFields = canUserModifyCustomers(permissions) || (canUserAddCustomers(permissions) && !customerId);
  const { paymentDepositsEnabled } = useFeatureFlags();

  // when users sign up via Squire, shops cannot edit some of their information while a few fields are always editable
  const squireOwnedFieldsDisabled = isSquireOwnedClient || !canModifyFields;
  const commonFieldsDisabled = !canModifyFields;
  const prepayTooltip = paymentDepositsEnabled
    ? t('customers:form.profile.prepay_tooltip_deposits_enabled')
    : t('customers:form.profile.prepay_tooltip_deposits_disabled');

  return (
    <div className="u-mv">
      <div>
        {isBlocked && <div className="u-ph+">
          <div className="c-customer-block-banner">
            <Icon glyph={GLYPHS.blockClient} size="small" />
            {t('customers:form.profile.client_blocked_banner')}
          </div>
        </div>}
        <div className="u-ph layout layout--flush layout--middle">
          <div className="layout__item u-1/3">
            <UploadAvatar name="avatar" who="client's" disabled={squireOwnedFieldsDisabled} />
          </div>
          <div className="layout__item u-2/3">
            <LockNotice display-if={commonFieldsDisabled} />
          </div>
        </div>
        <div className="c-paragraph u-ph+ u-mb-" display-if={rewardCounter.count}>
          <strong className="u-mr-- gamma" style={{ color: '#e84b88' }}>
            {rewardCounter.type === 'transaction' ? rewardCounter.count : <Money cents={rewardCounter.count} />}
          </strong>
          <Trans i18nKey="customers:form.profile.loyalty_reward_message">
            {rewardCounter.type === 'transaction' && 'transactions '}
            to loyalty reward.
          </Trans>
        </div>

        <div className="c-paragraph u-ph+ u-mb-" display-if={rewardCounter.lastRewardCodeUsed}>
          <h4 className="c-heading-info">{t('customers:form.profile.last_reward_code_label')}</h4>
          <strong>{rewardCounter.lastRewardCodeUsed.code}</strong> - {t('customers:form.profile.used_on_label')}{' '}
          <strong>
            {formatDateTime(
              rewardCounter.lastRewardCodeUsed.usedAt,
              rewardCounter.lastRewardCodeUsed.timezone || timezone
            )}
          </strong>
          <strong display-if={activeType === 'brand' && rewardCounter.lastRewardCodeUsed.shopName}>
            @ {rewardCounter.lastRewardCodeUsed.shopName}
          </strong>
        </div>

        <div
          className="c-paragraph u-ph+ u-mb"
          display-if={rewardCounter.unusedRewardCodes && rewardCounter.unusedRewardCodes.length}
        >
          <h4 className="c-heading-info">{t('customers:form.profile.unused_reward_codes_label')}</h4>
          {rewardCounter.unusedRewardCodes.map((rewardCode) => (
            <p key={rewardCode.code}>
              <strong>{rewardCode.code}</strong> - {t('customers:form.profile.issued_on_label')}{' '}
              <strong>{formatDateTime(rewardCode.issuedAt, timezone)}</strong>
            </p>
          ))}
        </div>
        {!paymentDepositsEnabled && (
          <div className="c-paragraph u-ph+ u-mb">
            <div className="o-flexbox o-flexbox__item--flex-start">
              <div className="o-flexbox o-flexbox__item--flex-start u-flex-wrap u-1/2-large-and-up">
                <div className="o-flexbox u-flex-1 u-min-width-fit-content o-flexbox--content-align-center o-flexbox--justify-start u-1/2-large-and-up u-mr-">
                  <CheckboxSwitch
                    name="blockNoShowFromBookNoPay"
                    label={t('customers:form.profile.only_prepay_label')}
                    wrapperClass="o-flexbox o-flexbox--content-align-center"
                    disabled={commonFieldsDisabled}
                  />
                  <Tooltip className="u-no-shrink">{prepayTooltip}</Tooltip>
                </div>
                <CheckboxSwitch
                  display-if={blockStatusCanBeChanged}
                  name="isBlocked"
                  label={t('customers:form.profile.block_customer_label')}
                  wrapperClass="u-min-width-fit-content u-1/2-large-and-up o-flexbox u-flex-1 o-flexbox--content-align-center"
                />
              </div>
              <div
                className="layout__item u-1/2-large-and-up"
                display-if={customerId && canUserModifyCustomers(permissions)}
              >
                <button type="button" onClick={paymentModalOn} className="c-btn c-btn--outline c-btn--small">
                  {t('customers:form.profile.payment_options_button')}
                </button>
                <PaymentOptionsModal
                  display-if={paymentModalIsOn}
                  close={paymentModalOff}
                  canChangeDefault
                  canDelete
                />
              </div>
            </div>
          </div>
        )}
        <BasicInfo
          customer
          leadAutoAssigned={leadAutoAssigned}
          disabled={squireOwnedFieldsDisabled}
          phoneFormatter={phoneFormatter}
        />
        <div className="c-wrapper-horizontal u-mb">
          <div className="layout u-mt">
            <div className="layout__item u-1/2-medium-and-up u-mt--">
              <div className="c-label-new">{t('customers:form.profile.birthday_label')}</div>
              <Input
                className="c-input fs-mask"
                type="text"
                placeholder={dateFormat}
                name="birthDate"
                datepicker={{ endDate: new Date(), startView: 2, defaultViewDate: { year: 1990, month: 0, day: 1 } }}
                disabled={squireOwnedFieldsDisabled}
              />
            </div>
            <div className="layout__item u-1/2-medium-and-up">
              <div className="c-label-new u-pb--">{t('customers:form.profile.notes_label')}</div>
              <Textarea
                className="c-textarea c-textarea--short"
                placeholder={t('customers:form.profile.notes_placeholder')}
                name="notes"
                disabled={commonFieldsDisabled}
              />
            </div>
          </div>
        </div>
        {paymentDepositsEnabled && (
          <div className="c-paragraph u-ph+ u-mb">
            <div className="c-heading c-heading--dark u-pv0 u-mb">{t('customers:form.profile.payment_settings_title')}</div>
            <CheckboxSwitch
              name="blockNoShowFromBookNoPay"
              id="blockNoShowFromBookNoPay"
              wrapperClass={styles.checkboxSwitch}
              label={
                <>
                  <TextM color="primary" className={styles.checkboxSwitchLabel}>
                    <Trans i18nKey="customers:form.profile.require_prepayment_label">
                      Require prepayment <Tag>Full no-show protection</Tag>
                    </Trans>
                  </TextM>
                  <TextM color="secondary">{t('customers:form.profile.require_prepayment_description')}</TextM>
                </>
              }
              disabled={commonFieldsDisabled}
            />
            <br />
            <CheckboxSwitch
              display-if={blockStatusCanBeChanged}
              name="isBlocked"
              id="isBlocked"
              wrapperClass={styles.checkboxSwitch}
              label={
                <div className={styles.checkboxSwitchLabelContainer}>
                  <TextM color="primary" className={styles.checkboxSwitchLabel}>
                    {t('customers:form.profile.block_self_booking_label')}
                  </TextM>
                  <TextM color="secondary">
                    {t('customers:form.profile.block_self_booking_description')}
                  </TextM>
                </div>
              }
            />
            <hr className="c-separator u-mt u-mb" />
            <div display-if={customerId && canUserModifyCustomers(permissions)} className="u-mb+">
              <div className="c-heading c-heading--small u-inline-block">{t('customers:form.profile.payment_methods_title')}</div>
              <div>{t('customers:form.profile.payment_methods_description')}</div>
              <button type="button" onClick={paymentModalOn} className="c-btn c-btn--outline c-btn--small u-mt--">
                {t('customers:form.profile.payment_options_button')}
              </button>
              <PaymentOptionsModal display-if={paymentModalIsOn} close={paymentModalOff} canChangeDefault canDelete />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

Profile.propTypes = {
  permissions: PropTypes.object.isRequired,
  timezone: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  rewardCounter: PropTypes.object,
  owner: PropTypes.bool,
  activeType: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  phoneFormatter: PropTypes.func,
  paymentModalOn: PropTypes.func.isRequired,
  paymentModalOff: PropTypes.func.isRequired,
  paymentModalIsOn: PropTypes.bool.isRequired,
  isBlocked: PropTypes.bool.isRequired,
  setIsBlocked: PropTypes.bool.isRequired,
  blockStatusCanBeChanged: PropTypes.bool.isRequired,
  leadAutoAssigned: PropTypes.bool.isRequired,
};

Profile.defaultProps = {
  rewardCounter: {},
  owner: false,
  phoneFormatter: (phone) => phone,
};

export default compose(
  withToggle('paymentModal', false),
  withState('isBlocked', 'setIsBlocked', (props) => props.initialValues.isBlocked),
  lifecycle({
    componentWillUpdate(nextProps) {
      if (this.props.initialValues.isBlocked !== nextProps.initialValues.isBlocked) {
        this.props.setIsBlocked(nextProps.initialValues.isBlocked);
      }
    },
  })
)(Profile);
